import baseApi from '@/repositories/base_api';
import { UUID } from '@/interfaces/uuid/uuid';
import { IRecipe } from '@/interfaces/recipe/recipe';
import { IAPIResponse } from '@/interfaces/api/response';
import { IRecipeForm } from '@/interfaces/recipe/recipe_form';


export const recipeApi = baseApi.injectEndpoints({
    endpoints: (build) => ({
        getRecipes: build.query<IRecipe[], void>({
            query: () => ({ url: `/library/recipes`, method: 'get' }),
            transformResponse: (response: IAPIResponse<IRecipe[]>) => response.data,
            providesTags: ['Recipe.list'],
            keepUnusedDataFor: 1800,
        }),
        getRecipe: build.query<IRecipe, UUID>({
            query: (id) => ({ url: `/library/recipes/${id}`, method: 'get' }),
            transformResponse: (response: IAPIResponse<IRecipe>) => response.data,
            providesTags: (result, error, id) => [{ type: 'Recipe', id }],
            keepUnusedDataFor: 1800
        }),
        addRecipe: build.mutation<IRecipe, IRecipeForm>({
            query: (data) => ({ url: '/library/recipes', method: 'post', data }),
            transformResponse: (response: IAPIResponse<IRecipe>) => response.data,
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                queryFulfilled.then((response) => dispatch(recipeApi.util.updateQueryData('getRecipes', undefined, recipes => {
                    recipes.push(response.data)
                }))).catch(() => {});
            },
        }),
        updateRecipe: build.mutation<IRecipe, {id: UUID, data: FormData}>({
            query: ({id, data}) => ({ url: `/library/recipes/${id}`, method: 'POST', data, headers: { 'Content-Type': 'multipart/form-data' } }),
            transformResponse: (response: IAPIResponse<IRecipe>) => response.data,
            async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
                const { data: updatedRecipe } = await queryFulfilled;
                dispatch(recipeApi.util.updateQueryData('getRecipes', undefined, recipes => {
                    const index = recipes.findIndex((item) => item.id === updatedRecipe.id);
                    if (index !== -1) {
                        recipes[index] = updatedRecipe;
                    }
                }));
                dispatch(recipeApi.util.updateQueryData('getRecipe', id, () => updatedRecipe));
            },
        }),
        copyRecipe: build.mutation<IRecipe, {id: UUID, name: string}>({
            query: ({id, name}) => ({ url: `/library/recipes/${id}/copy`, method: 'post', data: { name } }),
            transformResponse: (response: IAPIResponse<IRecipe>) => response.data,
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                const { data: copiedRecipe } = await queryFulfilled;
                dispatch(recipeApi.util.updateQueryData('getRecipes', undefined, recipes => {
                    recipes.push(copiedRecipe);
                }));
            }
        }),
        publishRecipe: build.mutation<IRecipe, UUID>({
            query: (id) => ({ url: `/library/recipes/${id}/publish`, method: 'post' }),
            transformResponse: (response: IAPIResponse<IRecipe>) => response.data,
            async onQueryStarted(id, { dispatch, queryFulfilled }) {
                queryFulfilled.then((response) => {
                    dispatch(recipeApi.util.updateQueryData('getRecipes', undefined, (recipes) => {
                        const item = recipes.find((item) => item.id === response.data.id)!;
                        item.published = response.data.published; 
                    }));
                    dispatch(recipeApi.util.updateQueryData('getRecipe', id, (recipe) => {
                        recipe.published = response.data.published; 
                    }));
                }).catch(() => {});
            },
        }),
        deleteRecipe: build.mutation<void, UUID>({
            query: (id) => ({ url: `/library/recipes/${id}`, method: 'delete' }),
            async onQueryStarted(id, { dispatch, queryFulfilled }) {
                queryFulfilled.then((_) =>
                dispatch(recipeApi.util.updateQueryData('getRecipes', undefined, (recipes) => {
                    const index = recipes.findIndex((item) => item.id === id)!;
                    recipes.splice(index, 1)
                }))).catch(() => {});
            },
        }),
    }),
})

export const {
    useGetRecipesQuery,
    useGetRecipeQuery,
    useLazyGetRecipeQuery,
    useAddRecipeMutation,
    useUpdateRecipeMutation,
    useCopyRecipeMutation,
    usePublishRecipeMutation,
    useDeleteRecipeMutation,
} = recipeApi;

